import { Shop as ShopComponent } from '@shared/components/ui/shop';
import { UserCard } from '@shared/components/ui/usercard';
import { SortConfig, VirtualTable } from '@components/ui/VirtualTable';
import { faBagShopping } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox } from '@shared/components/ui/checkbox';
import { usePermissions } from '@shared/hooks/usePermissions';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  OrderStatus,
  defaultLogisticStatusDot,
  defaultOrderStatusDot,
  getStatusForDropdown,
  logisticStatusColorMap,
  statusColorMap,
} from '../../config/orderStatusConfig';
import { StatusBadgeWithDot } from './StatusBadgeWithDot';
import { Order } from '../../models/Orders';
import { DropdownActions, SelectionToolbar } from '../interaction';
import { BulkPreparationModal, BulkPreparationModalRef, ProductsModal, ProductsModalRef } from '../modals';

type BulkActionResult = Promise<boolean> | boolean | void;

// ?? Función utilitaria para formatear fechas como dd/mm/yyyy HH:mm
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return '-';
  }
};

// ?? Función utilitaria para renderizar valores de forma segura
const safeRender = (value: any): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'boolean') return value.toString();

  // Si es un objeto, intentar extraer propiedades comunes
  if (typeof value === 'object') {
    if (value.name) return value.name;
    if (value.description) return value.description;
    if (value.title) return value.title;
    if (value.label) return value.label;
    if (value.id) return value.id.toString();
  }

  return '-';
};

const formatOrderTotal = (order: Order): string => {
  const amount = order.total;
  const currency = order.currency || 'EUR';
  if (amount == null || Number.isNaN(Number(amount))) return '-';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(amount));
  } catch {
    return `${amount} ${currency}`;
  }
};

interface TableProps {
  orders: Order[];
  isLoading?: boolean;
  className?: string;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  sorting?: SortConfig | null;
  onSortingChange?: (sorting: SortConfig | null) => void;
  selectedColumns?: string[];
  refetch?: () => void;
  // ?? Props para contadores de acciones
  cancelableCount?: number;
  preparableCount?: number;
  sendableCount?: number;
  uncancelableCount?: number;
  // ?? Props de selección
  selectedOrders?: Set<number>;
  onToggleOrderSelection?: (order: Order) => void;
  onSelectAllOrders?: () => void;
  onClearSelection?: () => void;
  // ?? Props para handlers de acciones
  onCancelSelected?: () => BulkActionResult;
  onPrepareSelected?: (preparationType: 'picking_packing' | 'massive_packing') => BulkActionResult;
  onMarkForSendSelected?: () => BulkActionResult;
  onUncancelSelected?: () => BulkActionResult;
}

export const Table = React.memo(function Table({
  orders,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  sorting,
  onSortingChange,
  selectedColumns,
  refetch,
  cancelableCount = 0,
  preparableCount = 0,
  sendableCount = 0,
  uncancelableCount = 0,
  selectedOrders,
  onToggleOrderSelection,
  onSelectAllOrders,
  onClearSelection,
  onCancelSelected,
  onPrepareSelected,
  onMarkForSendSelected,
  onUncancelSelected,
}: TableProps) {
  const { t } = useTranslation('ordersModule');
  const bulkPreparationModalRef = useRef<BulkPreparationModalRef>(null);

  // ?? Estados calculados para la selecci?n
  const areAllSelected = useMemo(
    () => orders.length > 0 && selectedOrders?.size === orders.length,
    [orders.length, selectedOrders?.size],
  );

  // ?? Funciones para manejar acciones en lote

  const handlePrepareSelected = useCallback(() => {
    if (preparableCount > 0) {
      bulkPreparationModalRef.current?.open(preparableCount);
    }
  }, [preparableCount]);

  const handleBulkPreparationConfirm = useCallback(
    (preparationType: 'picking_packing' | 'massive_packing') => {
      // Llamar al handler real pasado desde el padre
      onPrepareSelected?.(preparationType);
    },
    [onPrepareSelected],
  );

  const handleCancelSelected = useCallback(() => {
    // Llamar al handler real pasado desde el padre
    onCancelSelected?.();
  }, [onCancelSelected]);

  const handleMarkForSendSelected = useCallback(() => {
    // Llamar al handler real pasado desde el padre
    onMarkForSendSelected?.();
  }, [onMarkForSendSelected]);

  const handleUncancelSelected = useCallback(() => {
    // Llamar al handler real pasado desde el padre
    onUncancelSelected?.();
  }, [onUncancelSelected]);

  const handleCloseSelectionToolbar = useCallback(() => {
    // Limpiar selecci?n usando la funci?n espec?fica del padre
    if (onClearSelection) {
      onClearSelection();
    }
  }, [onClearSelection]);

  // Estado y referencia para ProductsModal
  const productsModalRef = useRef<ProductsModalRef>(null);

  // Handler para abrir el modal
  const handleOpenProductsModal = (order: Order) => {
    const showNotAssigned = order.status?.id === 1 || order.status?.id === 2;
    const payload = {
      orderNumber: order.ref,
      stockMessage:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      // Mapear l?neas para la modal de productos
      products: order.lines.map((line) => ({
        // Si el nombre de la l?nea viene nulo, usar el nombre del producto o t?tulo del pack
        // Esto corrige casos donde la API env?a line.name = null pero product.name s? existe
        name: line.name || line.product?.name || (line.pack ? line.pack.title : ''),
        image: line.product?.image_hash || '',
        badge: line.product?.code || (line.pack ? line.pack.title : ''),
        qty: line.qty,
        price: line.amount,
        subtotal: line.qty * line.amount,
      })),
      subtotal: order.lines.reduce((acc, line) => acc + line.qty * line.amount, 0),
      shipping: order.total_shipping,
      tax: order.total_tax,
      discount: order.total_discount,
      total: order.total,
      showNotAssignedAlert: showNotAssigned,
    } as const;
    productsModalRef.current?.openWithData(payload);
  };

  // Mapeo de c?digos de estado a estados del dropdown
  const getStatusForDropdownLocal = (statusCode: number): OrderStatus => {
    return getStatusForDropdown(statusCode);
  };

  // 🎯 Verificar si el usuario es 3PL (solo ellos ven la columna de Cliente/Team)
  const { getTeamType } = usePermissions();
  const hasTeamData = getTeamType() === 'tenant_3pl';

  const columns = [
    // ?? Nueva columna de selecci?n
    {
      header: () => (
        <div className="w-auto mx-auto">
          <Checkbox checked={areAllSelected} onCheckedChange={onSelectAllOrders} className="w-4 h-4" />
        </div>
      ),
      accessorKey: 'selection',
      size: 10,
      sortable: false,
      cell: ({ row }: any) => (
        <div className="flex items-center justify-start">
          <Checkbox
            checked={selectedOrders?.has(row.original.id) || false}
            onCheckedChange={() => onToggleOrderSelection?.(row.original)}
            className="w-4 h-4"
          />
        </div>
      ),
    },
    {
      header: t('table.order'),
      accessorKey: 'ref',
      size: 30,
      sortable: true,
      cell: ({ row }: any) => {
        const statusMapped = row.original.shipment?.status_mapped;

        if (!statusMapped) {
          return (
            <StatusBadgeWithDot
              className="text-xs"
              dotClassName={defaultLogisticStatusDot.dot}
              label={t('table.no_logistic_status')}
            />
          );
        }

        if (typeof statusMapped === 'string') {
          return (
            <StatusBadgeWithDot className="text-xs" dotClassName={defaultLogisticStatusDot.dot} label={statusMapped} />
          );
        }

        const { dot } = logisticStatusColorMap[statusMapped.id] ?? defaultLogisticStatusDot;
        return <StatusBadgeWithDot className="text-xs" dotClassName={dot} label={statusMapped.name} />;
      },
    },
    {
      header: t('table.products'),
      accessorKey: 'products',
      size: 20,
      sortable: false,
      cell: ({ row }: any) => {
        const isRed = row.original.status?.id === 1 || row.original.status?.id === 2 || row.original.status?.id === 9;
        const color = isRed ? 'bg-error' : 'bg-success';
        return (
          <span className="text-sm font-medium flex items-center gap-2 relative">
            <button
              type="button"
              className="relative inline-flex items-center gap-2 focus:outline-none hover:opacity-70"
              onClick={() => handleOpenProductsModal(row.original)}
              title="Ver productos"
            >
              <span className="relative inline-block">
                <FontAwesomeIcon icon={faBagShopping} className="w-3 h-3" />
                <span
                  className={`absolute -top-1 -right-0 w-1.5 h-1.5 mt-2.5 rounded-full border border-black ${color}`}
                ></span>
              </span>
              {row.original.lines?.reduce((acc: number, line: any) => acc + (line?.qty || 0), 0)}
            </button>
          </span>
        );
      },
    },
    {
      header: t('table.total'),
      accessorKey: 'total',
      size: 22,
      sortable: true,
      cell: ({ row }: any) => (
        <span className="text-sm font-medium tabular-nums">{formatOrderTotal(row.original)}</span>
      ),
    },
    {
      header: t('table.shop'),
      accessorKey: 'shop',
      size: 30,
      sortable: true,
      cell: ({ row }: any) => <ShopComponent name={row.original.shop.name} src={row.original.shop.image_hash} />,
    },
    {
      header: t('table.created_at'),
      accessorKey: 'order_created_at',
      size: 25,
      sortable: true,
      cell: ({ row }: any) => <span className="text-sm">{formatDate(row.original.order_created_at)}</span>,
    },
    {
      header: t('table.ready_to_prepare_at'),
      accessorKey: 'ready_to_prepare_at',
      size: 25,
      sortable: true,
      cell: ({ row }: any) => <span className="text-sm">{formatDate(row.original.ready_to_prepare_at)}</span>,
    },
    {
      header: t('table.status'),
      accessorKey: 'status',
      size: 25,
      sortable: false,
      cell: ({ row }: any) => {
        const code = row.original.status.id;
        const { dot } = statusColorMap[code] ?? defaultOrderStatusDot;
        return <StatusBadgeWithDot className="text-xs" dotClassName={dot} label={row.original.status.description} />;
      },
    },
    {
      header: t('table.courier'),
      accessorKey: 'courier',
      size: 30,
      sortable: false,
      cell: ({ row }: any) =>
        row.original.shipment.courier ? (
          <ShopComponent name={row.original.shipment.courier?.name} src={row.original.shipment.courier?.image_hash} />
        ) : (
          <span className="text-sm">-</span>
        ),
    },
    {
      header: t('table.customer'),
      accessorKey: 'customer',
      size: 30,
      sortable: false,
      cell: ({ row }: any) => <span className="text-sm font-medium">{safeRender(row.original.customer?.name)}</span>,
    },
    {
      header: t('table.shipment_date'),
      accessorKey: 'shipment_processed_at',
      size: 25,
      sortable: true,
      cell: ({ row }: any) => <span className="text-sm">{formatDate(row.original.shipment?.processed_at)}</span>,
    },
    {
      header: t('table.logistic_status'),
      accessorKey: 'logistic_status',
      size: 25,
      sortable: false,
      cell: ({ row }: any) => {
        const statusMapped = row.original.shipment?.status_mapped;

        if (!statusMapped) {
          return (
            <StatusBadgeWithDot
              className="text-xs"
              dotClassName={defaultLogisticStatusDot.dot}
              label={t('table.no_logistic_status')}
            />
          );
        }

        const { dot } = logisticStatusColorMap[statusMapped.id] ?? defaultLogisticStatusDot;
        return <StatusBadgeWithDot className="text-xs" dotClassName={dot} label={statusMapped.name} />;
      },
    },
    {
      header: t('table.tracking_number'),
      accessorKey: 'tracking_number',
      size: 25,
      sortable: false,
      cell: ({ row }: any) => (
        <span className="text-sm font-mono">{safeRender(row.original.shipment?.tracking_number)}</span>
      ),
    },
    {
      header: t('table.last_logistic_update'),
      accessorKey: 'date_tracking_update',
      size: 25,
      sortable: true,
      cell: ({ row }: any) => (
        <span className="text-sm">{formatDate(row.original.shipment?.date_tracking_update)}</span>
      ),
    },
    {
      header: t('table.payment_method'),
      accessorKey: 'payment_method',
      size: 25,
      sortable: false,
      cell: ({ row }: any) => <span className="text-sm">{safeRender(row.original.external_payment_method)}</span>,
    },
    {
      header: t('table.phone'),
      accessorKey: 'phone',
      size: 20,
      sortable: false,
      cell: ({ row }: any) => <span className="text-sm">{safeRender(row.original.customer?.phone)}</span>,
    },
    // ?? Columna de Cliente solo si hay datos de team
    ...(hasTeamData
      ? [
          {
            header: t('table.client'),
            accessorKey: 'team',
            size: 30,
            sortable: false,
            cell: ({ row }: any) =>
              row.original.team?.name ? (
                <UserCard name={row.original.team.name} email="" />
              ) : (
                <span className="text-sm text-complementary-text">-</span>
              ),
          },
        ]
      : []),
    // Columna de acciones siempre presente
    {
      header: '',
      accessorKey: 'actions',
      size: 15,
      sortable: false,
      cell: ({ row }: any) => {
        const status = getStatusForDropdownLocal(row.original.status.id);
        return (
          <DropdownActions
            status={status}
            orderId={row.original.id}
            orderNumber={row.original.ref}
            channelId={row.original.shop?.channel_id}
            onAction={refetch}
            attachments={row.original.attachments}
          />
        );
      },
    },
  ];

  // Filtrar columnas seg?n las seleccionadas (si se provee la prop)
  // La columna de selecci?n siempre debe estar presente
  const filteredColumns = selectedColumns
    ? [
        // La columna de selecci?n siempre va primero
        columns[0],
        // Luego las columnas filtradas (excluyendo la de selecci?n)
        ...columns.slice(1).filter((col) => selectedColumns.includes(col.accessorKey)),
      ]
    : columns;

  return (
    <div className="w-full">
      {/* ?? Indicador de selecci?n
      {areSomeSelected && (
        <div className="mb-2 p-2 bg-primary/10 border border-primary/20 rounded-lg">
          <span className="text-sm font-medium text-primary">
            {selectedRows.size} de {orders.length} pedidos seleccionados
          </span>
        </div>
      )} */}

      <VirtualTable
        data={orders}
        columns={filteredColumns}
        autoHeight={true}
        maxHeight={orders.length <= 10 ? `${orders.length * 70 + 120}px` : 'calc(100vh - 150px)'}
        minHeight="120px"
        rowHeight={70}
        emptyMessage={t('empty.message')}
        className="rounded-lg shadow-sm"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        infiniteScrollThreshold={500}
        sorting={sorting}
        onSortingChange={onSortingChange}
      />

      {/* ?? Toolbar de selección flotante */}
      <SelectionToolbar
        selectedCount={selectedOrders?.size || 0}
        onClose={handleCloseSelectionToolbar}
        onCancelSelected={handleCancelSelected}
        onPrepareSelected={handlePrepareSelected}
        onMarkForSendSelected={handleMarkForSendSelected}
        onUncancelSelected={handleUncancelSelected}
        cancelableCount={cancelableCount}
        preparableCount={preparableCount}
        sendableCount={sendableCount}
        uncancelableCount={uncancelableCount}
      />
      {/* Modal de productos (sin props para evitar re-renders) */}
      <ProductsModal ref={productsModalRef} />

      {/* Modal de preparación masiva */}
      <BulkPreparationModal ref={bulkPreparationModalRef} onConfirm={handleBulkPreparationConfirm} />
    </div>
  );
});
